import { useTranslation } from 'react-i18next';

interface VideoPlayerProps {
  title: string;
  videoUrl: string;
  onDownload?: (() => void) | null;
  showDownloadButton?: boolean;
}

export function VideoPlayer({ title, videoUrl, onDownload, showDownloadButton = true }: VideoPlayerProps) {
  const { t } = useTranslation();

  // Function to handle direct download
  const handleDirectDownload = () => {
    // Create a temporary link element
    const link = document.createElement('a');
    link.href = videoUrl;
    
    // Try to set download attribute (works on desktop browsers)
    link.setAttribute('download', t('videoPlayer.defaultFilename'));
    
    // Set target to _blank as fallback for mobile
    link.setAttribute('target', '_blank');
    
    // Add to DOM, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="bg-surface-interactive dark:bg-surface-interactive-dark border border-interactive dark:border-interactive-dark rounded-2xl overflow-hidden shadow-interactive">
      <div className="flex items-center justify-between p-4 border-b border-interactive dark:border-interactive-dark">
        <h3 className="text-lg font-semibold text-content-strong truncate max-w-[75%]" title={title}>
          {title}
        </h3>
        {showDownloadButton && (
          onDownload ? (
            <button 
              onClick={onDownload}
              className="p-2 text-primary-accent hover:bg-primary-accent-alpha-10 rounded-full transition-colors flex items-center gap-1"
              title={t('videoPlayer.downloadButtonTitle')}
            >
              <span className="material-symbols-outlined text-xl">download</span>
              <span className="text-sm hidden sm:inline">{t('videoPlayer.downloadButtonText')}</span>
            </button>
          ) : (
            <button 
              onClick={handleDirectDownload}
              className="p-2 text-primary-accent hover:bg-primary-accent-alpha-10 rounded-full transition-colors flex items-center gap-1"
              title={t('videoPlayer.downloadButtonTitle')}
            >
              <span className="material-symbols-outlined text-xl">download</span>
              <span className="text-sm hidden sm:inline">{t('videoPlayer.downloadButtonText')}</span>
            </button>
          )
        )}
      </div>
      <div className="relative aspect-video bg-media-canvas">
        <video 
          controls 
          src={videoUrl}
          poster={videoUrl + '?t=1'}
          className="w-full h-full object-contain"
        />
      </div>
    </div>
  );
} 